import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { airports } from "@/data/airports";
import { airportDetails } from "@/data/airportDetails";
import { getTerminalInfo } from "@/data/airlineTerminals";
import { getDiningInfo } from "@/data/airportDining";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Car, Train, ExternalLink, Info } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { SchemaMarkup, generateBreadcrumbSchema, generateFAQSchema } from "@/components/SchemaMarkup";
import { LiveWaitStrip } from "@/components/airport/LiveWaitStrip";
import { AppStoreCTA } from "@/components/AppStoreCTA";
import NotFound from "./NotFound";

const AirportParking = () => {
  const { code } = useParams<{ code: string }>();
  const upper = code?.toUpperCase() ?? "";
  const lower = code?.toLowerCase() ?? "";
  const airport = airports.find((a) => a.code === upper);
  const details = airportDetails[upper];
  const terminalInfo = getTerminalInfo(upper);
  const hasDining = Boolean(getDiningInfo(upper));

  if (!airport || !details) return <NotFound />;

  const cityName = airport.city.split(",")[0];
  const url = `https://airportbuddy.app/airports/${lower}/parking`;

  const lots = [
    { label: "Economy / long-term", price: details.parking.economy, best: "Trips of three days or more. Expect a shuttle ride to the terminal — add 15-20 minutes." },
    { label: "Terminal garage", price: details.parking.garage, best: "Short trips and early departures. Closest walk to check-in." },
    ...(details.parking.valet
      ? [{ label: "Valet", price: details.parking.valet, best: "Tight timing or a heavy bag load. Drop at the curb and go." }]
      : []),
  ];

  const faqs = [
    {
      question: `How much is parking at ${upper}?`,
      answer: `At ${airport.name}, economy parking runs about ${details.parking.economy} and the terminal garage about ${details.parking.garage}${details.parking.valet ? `, with valet at roughly ${details.parking.valet}` : ""}. Rates change and holiday pricing can be higher — confirm before you book.`,
    },
    {
      question: `What is the cheapest way to park at ${upper}?`,
      answer: `Economy or long-term parking at ${details.parking.economy} is the cheapest on-airport option. Off-airport lots nearby are often cheaper still, but factor in the extra shuttle time. If you're travelling light, ${details.transitOptions[0]} may beat parking entirely.`,
    },
    {
      question: `How early should I arrive at ${upper} if I'm parking?`,
      answer: `Add 20-30 minutes on top of your normal arrival time to find a space, ride the shuttle and reach check-in. Then check the live TSA wait for ${upper} — on a heavy morning, security alone can run 30+ minutes.`,
    },
    {
      question: `Is there public transport to ${upper}?`,
      answer: `Yes. Options include ${details.transitOptions.join(", ")}.`,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{`${upper} Airport Parking – Rates, Cheapest Lots & Economy Parking`}</title>
        <meta
          name="description"
          content={`${upper} parking costs: economy ${details.parking.economy}, garage ${details.parking.garage}. Compare lots, transit options and live TSA wait times at ${airport.name}.`.slice(0, 158)}
        />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`${upper} Airport Parking – Rates & Cheapest Lots`} />
        <meta property="og:description" content={`Parking prices and the cheapest options at ${airport.name}, plus live security wait times.`} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <SchemaMarkup schema={generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Airports", url: "/airports" },
        { name: upper, url: `/airports/${lower}` },
        { name: "Parking", url: `/airports/${lower}/parking` },
      ])} />
      <SchemaMarkup schema={generateFAQSchema(faqs)} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 pt-4 pb-12">
          <Breadcrumbs items={[
            { label: "Home", href: "/" },
            { label: "Airports", href: "/airports" },
            { label: upper, href: `/airports/${lower}` },
            { label: "Parking", href: `/airports/${lower}/parking` },
          ]} />

          <div className="max-w-3xl mx-auto space-y-6">
            <header className="space-y-2 mt-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                {upper} airport parking: rates and cheapest options
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                {airport.name} · {cityName}
              </p>
            </header>

            <Card className="rounded-xl shadow-md border-primary/30">
              <CardContent className="py-6 space-y-4">
                <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <Car className="h-6 w-6 text-primary" aria-hidden="true" />
                  What parking costs at {upper}
                </h2>
                <ul className="space-y-3">
                  {lots.map((lot) => (
                    <li key={lot.label} className="rounded-xl border p-4 space-y-1">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-base font-semibold text-foreground">{lot.label}</span>
                        <span className="text-lg font-bold text-primary shrink-0">{lot.price}</span>
                      </div>
                      <p className="text-base text-muted-foreground">{lot.best}</p>
                    </li>
                  ))}
                </ul>
                <p className="flex items-start gap-2 text-sm text-muted-foreground border-t pt-3">
                  <Info className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                  Rates are typical published prices and change with season and demand. Confirm current pricing
                  {terminalInfo ? (
                    <>
                      {" "}on{" "}
                      <a href={terminalInfo.officialUrl} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
                        the official {upper} site
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                      </a>
                    </>
                  ) : (
                    " on the official airport website"
                  )}
                  {" "}before you travel.
                </p>
              </CardContent>
            </Card>

            <LiveWaitStrip airportCode={upper} />

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-3">
                <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <Train className="h-6 w-6 text-primary" aria-hidden="true" />
                  Skipping the car at {upper}
                </h2>
                <ul className="list-disc list-inside space-y-1 text-base text-muted-foreground">
                  {details.transitOptions.map((option) => (
                    <li key={option}>{option}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <AppStoreCTA
              placement="airport_footer"
              context={upper}
              headline={`Parked at ${upper}? Don't guess the security line.`}
              subline={`Airport Buddy tracks the ${upper} wait and tells you when to head for the shuttle. Free on iPhone.`}
            />

            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-5">
                <h2 className="text-xl font-bold text-foreground">{upper} parking questions</h2>
                {faqs.map((f) => (
                  <div key={f.question} className="space-y-1">
                    <h3 className="text-base font-semibold text-foreground">{f.question}</h3>
                    <p className="text-base text-muted-foreground">{f.answer}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}`}>{upper} live wait times</Link>
              </Button>
              {terminalInfo && (
                <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                  <Link to={`/airports/${lower}/terminals`}>{upper} terminals by airline</Link>
                </Button>
              )}
              <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}/guide`}>Full {upper} guide</Link>
              </Button>
              {hasDining && (
                <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                  <Link to={`/airports/${lower}/dining`}>{upper} restaurants</Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AirportParking;
